import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import supplyContinuousDocumentService from '../../services/supplyContinuousDocumentService';
import Toast from '../../components/Common/Toast';
import TableWithMonthGroups from '../../components/Common/TableWithMonthGroups';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const SupplyContinuousDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedInitialData = useRef(false);
  const previousFilters = useRef({ statusFilter: 'all', searchTerm: '' });
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      previousFilters.current = { statusFilter, searchTerm };
      loadDocuments();
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousFilters.current = { statusFilter, searchTerm };
      return;
    }

    if (hasLoadedInitialData.current) {
      const filtersChanged = 
        previousFilters.current.statusFilter !== statusFilter ||
        previousFilters.current.searchTerm !== searchTerm;
      
      if (filtersChanged) {
        previousFilters.current = { statusFilter, searchTerm };
        loadDocuments();
      }
    }
  }, [statusFilter, searchTerm]);

  // Filtrado por proveedor en frontend (sin nuevo request)
  const filteredDocuments = !supplierSearch.trim()
    ? documents
    : documents.filter((d) => {
        const name = (d.supplier?.name || '').toLowerCase();
        return name.includes(supplierSearch.trim().toLowerCase());
      });

  const loadDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await supplyContinuousDocumentService.getDocuments(params);
      
      if (response.success && response.data) {
        setDocuments(response.data);
      }
    } catch (error) {
      console.error('Error al cargar documentos:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los documentos',
        type: 'error'
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Calcular estadísticas sobre los documentos filtrados
  const totalDocuments = filteredDocuments.length;
  const openDocuments = filteredDocuments.filter(d => d.status === 'open').length;
  const completedDocuments = filteredDocuments.filter(d => d.status === 'completed').length;
  const totalAmount = filteredDocuments.reduce((sum, d) => sum + (parseFloat(d.total_amount) || 0), 0);
  const totalPending = filteredDocuments.reduce((sum, d) => sum + (parseFloat(d.pending_amount) || 0), 0);
  const totalUsed = filteredDocuments.reduce((sum, d) => sum + (parseFloat(d.items_total) || 0), 0);

  const formatCurrency = (amount) => {
    return `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Abierto</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Completado</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Cancelado</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const handleDeleteDocument = async (document) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el documento "${document.code}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await supplyContinuousDocumentService.deleteDocument(document.id);
      if (response.success) {
        await loadDocuments();
        setToast({
          isOpen: true,
          message: response.message || 'Documento eliminado exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el documento',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      setToast({
        isOpen: true,
        message: 'Error al eliminar el documento',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Documentos por Suministro Continuo</h1>
                <p className="text-gray-600 mt-1">Gestión de documentos por suministro continuo (Productos y Servicios)</p>
              </div>
              <Link
                to="/supply-continuous-documents/create"
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
              >
                + Nuevo Documento
              </Link>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/purchase-orders" className="hover:text-teal-600">Compras</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Documentos por Suministro Continuo</span>
            </nav>

            {/* Filtros y Búsqueda — sin tipo de servicio; buscador por proveedor */}
            <div className="bg-white rounded-lg shadow-md p-5 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Buscador por proveedor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proveedor
                  </label>
                  <input
                    type="text"
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    placeholder="Buscar por nombre de proveedor..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                {/* Búsqueda por código */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Código, serie, número..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                {/* Filtro de Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="all">Todos</option>
                    <option value="open">Abierto</option>
                    <option value="completed">Completado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Total Documentos</div>
                <div className="text-2xl font-bold text-gray-800 mt-1">{totalDocuments}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Abiertos</div>
                <div className="text-2xl font-bold text-yellow-600 mt-1">{openDocuments}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Monto Total</div>
                <div className="text-2xl font-bold text-teal-600 mt-1">{formatCurrency(totalAmount)}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Saldo Pendiente</div>
                <div className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalPending)}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Documentos */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {loadingDocuments ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando documentos...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500 text-lg">No hay documentos por suministro continuo</p>
                <Link
                  to="/supply-continuous-documents/create"
                  className="mt-4 inline-block px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
                >
                  + Crear primer documento
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <TableWithMonthGroups
                  data={filteredDocuments}
                  getDateForGrouping={(doc) => doc.issue_date}
                  defaultCollapsed
                  columnCount={8}
                  emptyMessage={supplierSearch.trim() && filteredDocuments.length === 0 ? "Ningún documento coincide con el proveedor buscado" : "No hay documentos por suministro continuo"}
                  headerRow={
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proveedor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Emisión</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items Usados</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo Pendiente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  }
                  renderRow={(document) => {
                    const usedPercentage = document.total_amount > 0
                      ? ((document.items_total / document.total_amount) * 100).toFixed(1)
                      : 0;
                    return (
                      <tr key={document.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link to={`/supply-continuous-documents/${document.id}/edit`} className="text-sm font-mono text-teal-600 hover:text-teal-900">
                            {document.code}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900" title={document.supplier?.name || 'N/A'}>
                            {document.supplier?.name ? (document.supplier.name.length > 25 ? `${document.supplier.name.substring(0, 25)}...` : document.supplier.name) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateLocaleLima(document.issue_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {formatCurrency(document.total_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[100px]">
                              <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${usedPercentage}%` }}></div>
                            </div>
                            <span className="text-xs">{usedPercentage}%</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatCurrency(document.items_total)} / {formatCurrency(document.total_amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={document.pending_amount > 0.01 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                            {formatCurrency(document.pending_amount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(document.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link to={`/supply-continuous-documents/${document.id}/edit`} className="text-indigo-600 hover:text-indigo-900 mr-4">Editar</Link>
                          <button onClick={() => handleDeleteDocument(document)} disabled={loading} className="text-red-600 hover:text-red-900 disabled:opacity-50">Eliminar</button>
                        </td>
                      </tr>
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default SupplyContinuousDocuments;
